import { forwardRef, useEffect, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useTranslation from 'next-translate/useTranslation';
import { format, isValid } from 'date-fns';
import { de, enGB, it, fr } from 'date-fns/locale';
import { DatePickerProps, RefPropsType } from './DatePicker.types';
import { ButtonStyled, IconStyled } from './DatePicker.styles';

/*
 * DOCS: see root Readme -> Packages
 * */

export const DatePicker: React.FC<DatePickerProps> = ({
  className,
  onChange,
  currentDate,
  displayDateFormat = 'dd.MM.yyyy',
  ...props
}) => {
  const getSafeDate = (date?: Date) => (date && isValid(date) ? date : new Date());
  const [selectedDate, setSelectedDate] = useState(getSafeDate(currentDate));
  const { t, lang } = useTranslation('common');

  useEffect(() => {
    setSelectedDate(getSafeDate(currentDate));
  }, [currentDate]);

  if (lang === 'de') {
    registerLocale('de', de);
  } else if (lang === 'fr') {
    registerLocale('fr', fr);
  } else if (lang === 'it') {
    registerLocale('it', it);
  } else {
    registerLocale('en', enGB);
  }

  const CustomInput = forwardRef<HTMLButtonElement, RefPropsType>(
    ({ value, onClick }, ref) => {
      const baseDate = selectedDate || (value ? new Date(value) : undefined);
      const formattedSelected = baseDate ? format(baseDate, displayDateFormat) : '';
      const label =
        formattedSelected || t('tableReservationForm.placeholders.visitDate');

      return (
        <ButtonStyled onClick={onClick} ref={ref}>
          <span className="datepicker-label">{label}</span>
          <IconStyled name="CALENDAR" />
        </ButtonStyled>
      );
    }
  );
  CustomInput.displayName = 'CustomInput';

  return (
    <div className={className}>
      <ReactDatePicker
        wrapperClassName="ballenberg-datepicker"
        calendarClassName="ballenberg-datepicker__calendar"
        popperClassName="ballenberg-datepicker__popper"
        selected={selectedDate}
        locale={lang}
        dateFormat={displayDateFormat}
        placeholderText={t('tableReservationForm.placeholders.visitDate')}
        customInput={<CustomInput />}
        onChange={(date: Date, event) => {
          setSelectedDate(date);
          if (onChange) {
            onChange(date, event);
          }
        }}
        {...props}
      />
    </div>
  );
};
